import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      if (response.data.already_subscribed) {
        toast.info(response.data.message);
      } else {
        toast.success(response.data.message);
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#1A1915] border-t border-[#C9A961]/20 mt-20">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-[#2A2720] via-[#1A1915] to-[#2A2720] py-12 border-b border-[#C9A961]/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="playfair text-2xl md:text-3xl font-semibold text-white mb-3">
            Join our inner circle
          </h3>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Receive our exclusive recipes, behind-the-scenes looks at our production, and member-only offers.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre adresse email"
              data-testid="newsletter-email-input"
              className="flex-1 px-4 py-3 bg-[#1A1915] border border-[#C9A961]/30 text-white placeholder-gray-500 focus:border-[#C9A961] focus:outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="px-6 py-3 bg-gradient-to-r from-[#C9A961] to-[#E8D5A3] text-black font-semibold hover:from-[#E8D5A3] hover:to-[#C9A961] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Envoi...' : "S'inscrire"}
            </button>
          </form>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <div className="flex items-center justify-center md:justify-start mb-4">
              <div className="w-20 h-20 bg-[#1A1915] p-2">
                <img
                  src="./images/goldlogo.webp"
                  alt="Legato d'Oro"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed text-center md:text-left">
              More than just olive oil; it's a heritage advancing with power and harmony, gold flowing for 3000 years.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="playfair text-lg font-semibold gold-text mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/terroir" className="text-gray-400 hover:text-[#C9A961] transition-colors text-sm">
                  Our Terroir
                </Link>
              </li>
              <li>
                <Link to="/qualite-export" className="text-gray-400 hover:text-[#C9A961] transition-colors text-sm">
                  Quality & Export
                </Link>
              </li>
              <li>
                <Link to="/collection" className="text-gray-400 hover:text-[#C9A961] transition-colors text-sm">
                  Our Collections
                </Link>
              </li>
              <li>
                <Link to="/recipes" className="text-gray-400 hover:text-[#C9A961] transition-colors text-sm">
                  Recipes
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#C9A961] transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="playfair text-lg font-semibold gold-text mb-4">Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="text-gray-400">
                <span className="text-[#C9A961]">Email:</span> contact@legatodoro.com
              </li>
              <li className="text-gray-400">
                <span className="text-[#C9A961]">Phone:</span> +216 36 400 920
              </li>
              <li className="text-gray-400">
                <span className="text-[#C9A961]">Address:</span> Z.I. Sidi Salem, route Gabes km3, 3000 Sfax, Tunisia
              </li>
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="playfair text-lg font-semibold gold-text mb-4">Follow Us</h4>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full border-2 border-[#C9A961] flex items-center justify-center text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-all duration-300"
                data-testid="social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full border-2 border-[#C9A961] flex items-center justify-center text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-all duration-300"
                data-testid="social-instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="w-10 h-10 rounded-full border-2 border-[#C9A961] flex items-center justify-center text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-all duration-300"
                data-testid="social-twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#C9A961]/20 mt-12 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 Legato d'Oro. All rights reserved. | Crafted with passion in Tunisia
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
